import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import { CorporateService } from "../models/Service";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks";
import { useTranslation } from "react-i18next";
import BrandMark from "./brandMark";

// The structural minimum this card renders, rather than the whole service. A
// CorporateService satisfies it unchanged, and so does a catalogue row, which
// carries only what a card needs and would otherwise have to be cast.
export type ServiceCardService = Pick<
  CorporateService,
  "id" | "service_name" | "service_type"
> & {
  // Optional because the catalogue contract marks it so; the tenant service
  // type still satisfies this by carrying it.
  service_description?: string;
  logo?: { url: string };
};

// The click dummy's two badge tints. Semantic, not brand: they say what kind of
// service this is, and the theme has no slot for that.
const BADGES: Record<string, { key: string; bg: string; border: string; ink: string }> = {
  SCHOOLFEE: {
    key: "serviceType.schoolfee",
    bg: "#EEF4FF",
    border: "#D5E2FB",
    ink: "#264A8C",
  },
  DEPOSITFUNDS: {
    key: "serviceType.deposit",
    bg: "#FFF4E5",
    border: "#F6DDB8",
    ink: "#7A4A00",
  },
};

const ServiceCard = ({
  service,
  subsidiaryName,
  onSelect,
}: {
  service: ServiceCardService;
  // Rendered only at platform scope, where a payer sees every school at once
  // and cannot otherwise tell whose service this is.
  subsidiaryName?: string;
  // The platform front door cannot use the relative navigation below: from `/`
  // it would resolve to `/:id`, which the greedy tenant route reads as a school
  // name. Supplying this lets the caller resolve the owning school first.
  onSelect?: (service: ServiceCardService) => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const primary = theme.color_primary;
  const badge = service.service_type ? BADGES[service.service_type] : undefined;

  const onSelectService = () => {
    if (onSelect) {
      onSelect(service);
      return;
    }
    navigate(`${service.id}`);
  };

  return (
    <Tooltip title={service?.service_name}>
      <Card
        data-testid="service-card"
        onClick={onSelectService}
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: "background.paper",
          borderColor: "divider",
          borderRadius: 3.5,
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:hover": {
            borderColor: primary,
            boxShadow: `0 0 0 4px ${alpha(primary, 0.24)}`,
          },
        }}
      >
        <CardActionArea
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 1.5,
            p: 1.75,
            height: "100%",
          }}
        >
          <BrandMark
            name={subsidiaryName || service.service_name}
            logoUrl={service.logo?.url}
            size={44}
            radius="12px"
          />

          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              flex: 1,
              minWidth: 0,
              p: 0,
              "&:last-child": { pb: 0 },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 15,
                lineHeight: 1.3,
                width: "100%",
                overflowWrap: "anywhere",
              }}
            >
              {service.service_name}
            </Typography>

            {service.service_description && (
              <Typography
                sx={{
                  fontSize: 13.5,
                  lineHeight: 1.35,
                  color: "text.secondary",
                  width: "100%",
                }}
              >
                {service.service_description}
              </Typography>
            )}

            {(subsidiaryName || badge) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {subsidiaryName && (
                  <Box
                    component="span"
                    data-testid="service-card-subsidiary"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {subsidiaryName}
                  </Box>
                )}
                {badge && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      lineHeight: 1.6,
                      borderRadius: "999px",
                      px: 1,
                      py: "1px",
                      whiteSpace: "nowrap",
                      bgcolor: badge.bg,
                      border: `1px solid ${badge.border}`,
                      color: badge.ink,
                    }}
                  >
                    {t(badge.key)}
                  </Box>
                )}
              </Box>
            )}
          </CardContent>

          <ChevronRightOutlined
            aria-hidden
            sx={{ color: "text.secondary", flexShrink: 0 }}
          />
        </CardActionArea>
      </Card>
    </Tooltip>
  );
};

export default ServiceCard;
